/**
 * MKS-314 — the single source of truth for the SPA's FORMATTING locale: how a
 * number is written, and how two strings sort.
 *
 * Before this file the app passed a literal locale tag to `toLocaleString` /
 * `Intl.NumberFormat` in ~45 places, so a company outside the Czech Republic
 * saw `1 234,50` whatever it had configured. Everything now goes through the
 * helpers below, which read the one module-level locale `useDateFormatSync`
 * writes from the instance's `instance_locale` setting.
 *
 * Deliberately the same shape as `utils/dateFormat.ts`: ONE mutable
 * module-level value, not a constant and not a React context. Numbers are
 * formatted from ~45 plain functions — table cell renderers, chart tick
 * formatters, `formatMoney` — none of which can call a hook, and threading a
 * locale argument through every one of them would be the ~100-call-site change
 * this ticket exists to avoid repeating.
 *
 * # The legacy locale is a per-call-site argument, not one constant
 *
 * This is the part that is easy to get wrong, and this file got it wrong once.
 *
 * The pre-MKS-314 codebase did not have ONE hardcoded locale. It had two: the
 * older surfaces wrote `cs-CZ` (`300 000 Kč`) and the newer ones — the revenue
 * KPIs, the manager overview, the invoice breakdown — wrote `en-US`
 * (`CZK 300,000`). Collapsing both onto a single fallback changes what
 * production renders while the release flag is still OFF, which is exactly
 * what the flag exists to prevent.
 *
 * So every helper takes the caller's OWN pre-ticket locale as its last
 * argument, defaulting to {@link LEGACY_CZ_NUMBER_LOCALE}. A site that used to
 * write `en-US` passes {@link LEGACY_EN_NUMBER_LOCALE} and therefore renders
 * identically in the flag's OFF branch. Passing the exported CONSTANT rather
 * than a bare `'en-US'` is what keeps those call sites clean under the
 * `localeFormatGuard` source scan.
 *
 * # Scope
 *
 * FORMATTING ONLY. Translating the UI is explicitly out of the ticket's scope;
 * the locale picks separators, digit grouping and collation — never a
 * language.
 */

/** What the older surfaces formatted with before MKS-314 — `1 234,50`. */
export const LEGACY_CZ_NUMBER_LOCALE = 'cs-CZ';

/** What the newer surfaces formatted with before MKS-314 — `1,234.50`. */
export const LEGACY_EN_NUMBER_LOCALE = 'en-US';

/**
 * The instance locale, or `null` when there is none in force.
 *
 * `null` — not a string default — is what makes the per-call-site legacy
 * argument work. It is the state while the MKS-314 flag is off, before the
 * instance-settings query resolves, for an unauthenticated visitor, and in any
 * component test that mounts a page on its own. In every one of those, each
 * call site must fall back to ITS OWN pre-ticket locale, which only the caller
 * knows.
 */
let activeNumberLocale: string | null = null;

/**
 * The locale in force for a caller whose pre-ticket locale was `fallback`.
 *
 * Callers that genuinely want to know whether an instance locale is set at all
 * should read {@link hasInstanceNumberLocale} instead of comparing against a
 * fallback they passed in.
 */
export function getActiveNumberLocale(fallback: string = LEGACY_CZ_NUMBER_LOCALE): string {
  return activeNumberLocale ?? fallback;
}

/** Is an instance locale in force, as opposed to each call site's legacy one? */
export function hasInstanceNumberLocale(): boolean {
  return activeNumberLocale !== null;
}

/**
 * Swap the app-wide number locale, or pass `null` to hand every call site back
 * to its own legacy locale. Called from `useDateFormatSync`, the one place
 * that reads the instance locale setting and the MKS-314 release flag.
 *
 * Tests may call it directly, and must reset it to `null` afterwards — the
 * value is module state shared by every test in a file.
 */
export function setActiveNumberLocale(locale: string | null): void {
  activeNumberLocale = locale;
}

/**
 * Format a number in the active locale, falling back to the caller's own
 * pre-ticket locale.
 *
 * Never throws. `Intl` rejects a malformed locale tag with a `RangeError`, and
 * a number cell that crashes the render is a far worse failure than one
 * rendered with the wrong separator — so an unusable locale degrades to the
 * caller's fallback, matching `formatCurrencyStrict`'s existing never-throw
 * contract in `utils/format.ts`.
 *
 * A non-finite value renders as the em-dash placeholder `formatFte` already
 * uses, rather than the literal `"NaN"` `toLocaleString` would otherwise print
 * into a money column.
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
  fallback: string = LEGACY_CZ_NUMBER_LOCALE
): string {
  if (!Number.isFinite(value)) return '—';
  try {
    return value.toLocaleString(getActiveNumberLocale(fallback), options);
  } catch {
    return value.toLocaleString(fallback, options);
  }
}

/**
 * Format a number in a NAMED locale, ignoring the active one.
 *
 * The one legitimate caller is a control that has to show what each locale
 * would look like — the admin page's formatting picker, which labels every
 * option with a sample. Everything that renders a real figure uses
 * {@link formatNumber} and follows the instance setting; a call site that
 * reaches for this one to render data is the bug this module exists to
 * prevent.
 *
 * Lives here, not in the page, so the sample comes out of the same code path
 * the app formats with and cannot drift from it — and so the page needs no
 * literal `Intl` call of its own.
 */
export function formatNumberInLocale(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions
): string {
  if (!Number.isFinite(value)) return '—';
  try {
    return value.toLocaleString(locale, options);
  } catch {
    return value.toLocaleString(LEGACY_CZ_NUMBER_LOCALE, options);
  }
}

/**
 * `Intl.NumberFormat` in the active locale, for a caller that formats many
 * values and wants to build the formatter once (chart tick formatters, long
 * table columns). Same never-throw contract as {@link formatNumber}.
 */
export function numberFormatter(
  options?: Intl.NumberFormatOptions,
  fallback: string = LEGACY_CZ_NUMBER_LOCALE
): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat(getActiveNumberLocale(fallback), options);
  } catch {
    return new Intl.NumberFormat(fallback, options);
  }
}

/**
 * Compare two strings for sorting, in the active locale.
 *
 * Collation shares the formatting locale rather than getting a setting of its
 * own: the lists this sorts are people's and projects' names, and a company
 * that configured `de-DE` wants `Ö` filed where German files it, for the same
 * reason it wants `1.234,50`. Czech collation puts `Ch` after `H`, German does
 * not, and the browser's own locale is the one thing that must NOT decide it —
 * two colleagues would then see the same timesheet in two different orders.
 *
 * Never throws; an unusable locale falls back to the caller's own.
 */
export function compareText(
  a: string,
  b: string,
  fallback: string = LEGACY_CZ_NUMBER_LOCALE
): number {
  try {
    return a.localeCompare(b, getActiveNumberLocale(fallback));
  } catch {
    return a.localeCompare(b, fallback);
  }
}
